// frontend/src/modules/reports/pages/workforce/ExceptionsPage.tsx
//
// Phase 4 (Reports Frontend), Task 2. Attendance exceptions (late / missed /
// short_day / early_departure) — follows the HoursSummaryPage reference
// pattern: ReportShell owns date range + CSV chrome, this page owns fetching
// (useApi keyed on the resolved range + type/employee filters), stat tiles
// per exception type, and a basis-aware table.
//
// `basis` honesty rule (same as HoursSummaryPage): "expected_*" columns come
// from a real shift schedule OR the company-hours/employee-default fallback
// — the header note must say "Against company hours" (never "Against
// schedule") when the tenant has scheduling off.

import * as React from 'react';
import { AlertTriangle, Clock3, LogOut, TimerReset } from 'lucide-react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { StatCard } from '../../../../design-system/components/StatCard';
import { DataTable } from '../../../../design-system/components/DataTable';
import { useApi } from '../../../../design-system/useApi';
import { reportUrl, downloadReportCsv } from '../../api';
import type { ExceptionsReport, ExceptionType } from '../../types';
import { VISUAL, statusPill, type StatusTone } from '../../../../design-system/visual';
import { formatCalendarDate } from '../../../../design-system/format';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../../components/ui/select';
import type { EmployeeWithUser } from '../../../hr/types';

type TypeFilter = 'all' | ExceptionType;

const TYPE_FILTERS: TypeFilter[] = ['all', 'late', 'missed', 'short_day', 'early_departure'];

const TYPE_LABELS: Record<ExceptionType, string> = {
  late: 'Late',
  missed: 'Missed',
  short_day: 'Short day',
  early_departure: 'Early departure',
};

const TYPE_TONES: Record<ExceptionType, StatusTone> = {
  late: 'warning',
  missed: 'destructive',
  short_day: 'info',
  early_departure: 'muted',
};

/** '125' minutes -> '2h 5m'; null -> '—'. */
function fmtMinutes(minutes: number | null): string {
  if (minutes === null) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function ExceptionsPage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);
  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>('all');
  const [employeeId, setEmployeeId] = React.useState<string>('all');

  const empResult = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = empResult.data?.employees ?? [];

  const path = range
    ? reportUrl('exceptions', {
      from: range.from,
      to: range.to,
      employeeId: employeeId !== 'all' ? employeeId : undefined,
      type: typeFilter !== 'all' ? typeFilter : undefined,
    })
    : null;
  const result = useApi<{ report: ExceptionsReport }>(path, [typeFilter, employeeId]);
  const forbidden = result.status === 403;

  const report = result.data?.report ?? null;
  const rows = report?.rows ?? [];
  const basis = report?.basis;
  const basisNote = basis === 'company_hours' ? 'Against company hours' : 'Against schedule';

  const counts = React.useMemo(() => {
    const c: Record<ExceptionType, number> = { late: 0, missed: 0, short_day: 0, early_departure: 0 };
    for (const r of rows) c[r.type] += 1;
    return c;
  }, [rows]);

  return (
    <ReportShell
      eyebrow="Reports / Workforce"
      title="Exceptions"
      description={`Attendance exceptions per employee. ${basisNote}.`}
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      filters={
        <>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
            {TYPE_FILTERS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTypeFilter(t)}
                className={[
                  'px-3.5 h-8 rounded-full text-[12px] font-semibold capitalize transition-colors',
                  typeFilter === t ? 'bg-saffron text-zinc-950' : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                {t === 'all' ? 'All' : TYPE_LABELS[t]}
              </button>
            ))}
          </div>
          {employees.length > 0 && (
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="h-9 rounded-full bg-card/80 border-border/60 px-3.5 text-[12.5px] font-semibold w-auto min-w-[180px]">
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </>
      }
      csv={range ? {
        filename: `exceptions-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'exceptions',
          { from: range.from, to: range.to, employeeId: employeeId !== 'all' ? employeeId : undefined, type: typeFilter !== 'all' ? typeFilter : undefined },
          `exceptions-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Late" value={counts.late} icon={<Clock3 />} />
            <StatCard label="Missed" value={counts.missed} icon={<AlertTriangle />} />
            <StatCard label="Short day" value={counts.short_day} icon={<TimerReset />} />
            <StatCard label="Early departure" value={counts.early_departure} icon={<LogOut />} />
          </div>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable
              rows={rows}
              rowKey={(r) => `${r.employee_id}-${r.date}-${r.type}`}
              columns={[
                { key: 'date', header: 'Date', cell: (r) => formatCalendarDate(r.date) },
                { key: 'employee', header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
                { key: 'type', header: 'Type', cell: (r) => <span className={statusPill(TYPE_TONES[r.type])}>{TYPE_LABELS[r.type]}</span> },
                { key: 'expected', header: basisNote, cell: (r) => (r.expected_start && r.expected_end ? `${r.expected_start}–${r.expected_end}` : '—') },
                { key: 'active', header: 'Active', cell: (r) => fmtMinutes(r.active_minutes) },
                { key: 'minutes_late', header: 'Minutes late', cell: (r) => (r.type === 'late' ? fmtMinutes(r.minutes_late) : '—') },
              ]}
            />
          </div>
        </>
      )}
    </ReportShell>
  );
}
